// components/customer/AppointmentHistory.js
import React, { useMemo, useState } from 'react';
import { Badge } from '../common';
import { useTranslations } from '../../hooks/useTranslations';
import { NoDataChart } from '../charts/NoDataChart';

export function AppointmentHistory({ appointments = [], loading = false, onAppointmentClick }) {
  const t = useTranslations();
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' for oldest first, 'desc' for newest first

  const handleAppointmentClick = (appointment) => {
    if (onAppointmentClick) {
      onAppointmentClick(appointment);
    }
  };

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Sort appointments by date based on sort direction
  const sortedAppointments = useMemo(() => {
    return [...appointments].sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [appointments, sortDirection]);

  // Format date and time for display
  const formatDateTime = (date, time) => {
    const appointmentDate = new Date(`${date}T${time}`);
    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    return appointmentDate.toLocaleDateString('he-IL', options).replace(',', ` ${t.at} `);
  };

  // Format price
  const formatPrice = (price) => {
    return `${t.currencySymbol}${parseFloat(price).toFixed(2)}`;
  };

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'cancelled':
        return 'danger';
      case 'no_show':
        return 'warning';
      case 'no_show_justified':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <h2 className="text-lg font-semibold text-gray-900">{t.appointmentHistory}</h2>
        </div>
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (sortedAppointments.length === 0) {
    return (
      <NoDataChart
        primaryMessage={t.noAppointmentsYet}
        secondaryMessage={t.thisCustomerHasntBookedAnyAppointments}
        dataType="data"
      />
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <h2 className="text-lg font-semibold text-gray-900">{t.appointmentHistory}</h2>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="w-1/6 text-right py-3 px-4 font-medium text-gray-700">{t.service}</th>
              <th
                className="w-1/6 text-right py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-50 transition-colors select-none"
                onClick={toggleSortDirection}
                title={sortDirection === 'asc' ? 'Click to sort newest first' : 'Click to sort oldest first'}
              >
                <div className="flex items-center justify-end space-x-1">
                  <span>{t.dateAndTime}</span>
                  <span className="text-sm">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                </div>
              </th>
              <th className="w-1/6 text-right py-3 px-4 font-medium text-gray-700">{t.crew}</th>
              <th className="w-1/6 text-right py-3 px-4 font-medium text-gray-700">{t.status}</th>
              <th className="w-1/6 text-right py-3 px-4 font-medium text-gray-700">{t.price}</th>
              <th className="w-1/6 text-right py-3 px-4 font-medium text-gray-700">{t.notes}</th>
            </tr>
          </thead>
          <tbody>
            {sortedAppointments.map((appointment) => (
              <tr
                key={appointment.id}
                className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleAppointmentClick(appointment)}
              >
                <td className="w-1/6 py-4 px-4">
                  <div className="font-medium text-gray-900 text-right">
                    {appointment.service_name}
                  </div>
                </td>
                <td className="w-1/6 py-4 px-4">
                  <div className="text-gray-900 text-right">
                    {formatDateTime(appointment.date, appointment.time)}
                  </div>
                </td>
                <td className="w-1/6 py-4 px-4">
                  <div className="text-gray-900">
                    {appointment.crew_member_name || t.dash}
                  </div>
                </td>
                <td className="w-1/6 py-4 px-4">
                  <div className="text-right">
                    <Badge variant={getStatusVariant(appointment.status)} size="small">
                      {t.statusMapping[appointment.status] || appointment.status}
                    </Badge>
                  </div>
                </td>
                <td className="w-1/6 py-4 px-4">
                  <div className="font-medium text-gray-900 text-right">
                    {formatPrice(appointment.price)}
                  </div>
                </td>
                <td className="w-1/6 py-4 px-4">
                  <div className="text-xs text-gray-500 text-right whitespace-pre-wrap break-words max-w-32 leading-relaxed">
                    {appointment.notes || t.dash}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden">
        {/* Sort Control for Mobile */}
        <div className="mb-4">
          <button
            onClick={toggleSortDirection}
            className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors px-3 py-2 rounded-md hover:bg-gray-50"
          >
            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
            <span>{sortDirection === 'asc' ? t.oldestFirst || 'Oldest First' : t.newestFirst || 'Newest First'}</span>
          </button>
        </div>

        <div className="space-y-4" dir="rtl">
          {sortedAppointments.map((appointment) => (
          <div
            key={appointment.id}
            className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => handleAppointmentClick(appointment)}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="font-medium text-gray-900" style={{ direction: 'ltr', textAlign: 'left' }}>
                {appointment.service_name}
              </div>
              <div className="font-medium text-gray-900 text-right">
                {formatPrice(appointment.price)}
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2 text-right">
              {formatDateTime(appointment.date, appointment.time)}
            </div>
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600" style={{ direction: 'ltr', textAlign: 'left' }}>
                {appointment.crew_member_name || t.noCrewAssigned}
              </div>
              <div>
                <Badge variant={getStatusVariant(appointment.status)} size="small">
                  {t.statusMapping[appointment.status] || appointment.status}
                </Badge>
              </div>
              {appointment.notes && (
                <div className="text-xs text-gray-500 mt-2 text-right">
                  <span className="font-medium">{t.notes}:</span>
                  <div className="whitespace-pre-wrap break-words mt-1 leading-relaxed">
                    {appointment.notes}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}